import type {
  DepthBucketsResponse,
  EffectiveSpreadBucketsResponse,
  LpMarkoutResponse,
  DexExecCostResponse,
  DexExecCostBucketRow,
  MandateSummaryResponse,
  QuoteFreshnessResponse,
  QuotedSpreadBucketsResponse,
  RealizedSpreadBucketsResponse,
  RecentEffectiveSpreadResponse,
} from "./types.ts";

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status?: number,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

// Origin-relative: the SPA is served from the same host as the API. NGINX (prod)
// or the Vite dev proxy forwards /api/* and /health to FastAPI.
async function getJson<T>(
  path: string,
  params: Record<string, string | number>,
): Promise<T> {
  const qs = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) qs.set(k, String(v));
  const res = await fetch(`${path}?${qs.toString()}`, {
    headers: { Accept: "application/json" },
  });
  if (!res.ok) {
    throw new ApiError(`Request failed (${res.status})`, res.status);
  }
  return (await res.json()) as T;
}

export interface RecentParams {
  venue: string;
  pair: string;
  clientId?: string;
  limit?: number;
  lookbackMinutes?: number;
}

export function fetchEffectiveSpread(
  p: RecentParams,
): Promise<RecentEffectiveSpreadResponse> {
  const params: Record<string, string | number> = { venue: p.venue, pair: p.pair };
  if (p.clientId) params.client_id = p.clientId;
  if (p.limit != null) params.limit = p.limit;
  if (p.lookbackMinutes != null) params.lookback_minutes = p.lookbackMinutes;
  return getJson<RecentEffectiveSpreadResponse>(
    "/api/v1/effective-spread/recent",
    params,
  );
}

// --- Bucketed aggregates (the box-whisker panels) ----------------------------
// window (lookbackMinutes) and resolution (bucketSeconds) are independent. No
// row limit on the server -- every observation in the window contributes.
export interface BucketParams {
  venue: string;
  pair: string;
  clientId?: string;
  lookbackMinutes?: number;
  bucketSeconds?: number;
}

function bucketQuery(p: BucketParams): Record<string, string | number> {
  const params: Record<string, string | number> = { venue: p.venue, pair: p.pair };
  if (p.clientId) params.client_id = p.clientId;
  if (p.lookbackMinutes != null) params.lookback_minutes = p.lookbackMinutes;
  if (p.bucketSeconds != null) params.bucket_seconds = p.bucketSeconds;
  return params;
}

export function fetchEffectiveSpreadBuckets(
  p: BucketParams,
): Promise<EffectiveSpreadBucketsResponse> {
  return getJson<EffectiveSpreadBucketsResponse>(
    "/api/v1/effective-spread/buckets",
    bucketQuery(p),
  );
}

export function fetchRealizedSpreadBuckets(
  p: BucketParams,
): Promise<RealizedSpreadBucketsResponse> {
  return getJson<RealizedSpreadBucketsResponse>(
    "/api/v1/realized-spread/buckets",
    bucketQuery(p),
  );
}

export function fetchQuotedSpreadBuckets(
  p: BucketParams,
): Promise<QuotedSpreadBucketsResponse> {
  return getJson<QuotedSpreadBucketsResponse>(
    "/api/v1/quoted-spread/buckets",
    bucketQuery(p),
  );
}

export interface FreshnessParams {
  venue: string;
  pair: string;
  clientId?: string;
}

export function fetchQuoteFreshness(
  p: FreshnessParams,
): Promise<QuoteFreshnessResponse> {
  const params: Record<string, string | number> = { venue: p.venue, pair: p.pair };
  if (p.clientId) params.client_id = p.clientId;
  return getJson<QuoteFreshnessResponse>("/api/v1/quote-freshness", params);
}

export interface MandateParams {
  venue: string;
  pair: string;
  clientId?: string;
}

export function fetchMandateSummary(
  p: MandateParams,
): Promise<MandateSummaryResponse> {
  const params: Record<string, string | number> = { venue: p.venue, pair: p.pair };
  if (p.clientId) params.client_id = p.clientId;
  return getJson<MandateSummaryResponse>("/api/v1/mandate-summary", params);
}



export function fetchDexExecCost(p: MandateParams): Promise<DexExecCostResponse> {
  const params: Record<string, string | number> = { venue: p.venue, pair: p.pair };
  if (p.clientId) params.client_id = p.clientId;
  return getJson<DexExecCostResponse>("/api/v1/dex-exec-cost", params);
}

export function fetchDexExecCostBuckets(
  p: BucketParams,
): Promise<DexExecCostBucketRow[]> {
  return getJson<DexExecCostBucketRow[]>("/api/v1/dex-exec-cost/series", bucketQuery(p));
}

export function fetchLpMarkout(
  p: MandateParams,
): Promise<LpMarkoutResponse> {
  const params: Record<string, string | number> = { venue: p.venue, pair: p.pair };
  if (p.clientId) params.client_id = p.clientId;
  return getJson<LpMarkoutResponse>("/api/v1/lp-markout", params);
}


// --- Book-L2 depth buckets (book_l2_depth_buckets, the §5 depth precompute) ---
// Market-grain: clientId is sent for echo/context only; the endpoint does NOT
// filter on it. bucketSeconds selects the cadence (300 standing, 60 forensic).
export interface DepthParams {
  venue: string;
  pair: string;
  clientId?: string;
  limit?: number;
  lookbackMinutes?: number;
  bucketSeconds?: number;
}

export function fetchDepthBuckets(
  p: DepthParams,
): Promise<DepthBucketsResponse> {
  const params: Record<string, string | number> = { venue: p.venue, pair: p.pair };
  if (p.clientId) params.client_id = p.clientId;
  if (p.limit != null) params.limit = p.limit;
  if (p.lookbackMinutes != null) params.lookback_minutes = p.lookbackMinutes;
  if (p.bucketSeconds != null) params.bucket_seconds = p.bucketSeconds;
  return getJson<DepthBucketsResponse>("/api/v1/book-l2-depth/buckets", params);
}
